import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { UserContext } from "../context/UserContext";
import Footer from "../FooterBn";
import "../styles/UserProfile.module.css";

function UserProfile() {
  const { user, logoutUser } = useContext(UserContext);
  const [error, setError] = useState("");
  const [reviews, setReviews] = useState({
    teacherReviews: [],
    instituteReviews: [],
  });
  const [editReview, setEditReview] = useState(null);
  const [updatedReview, setUpdatedReview] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      logoutUser();
      navigate("/login");
      console.log("User logged out successfully!");
    } catch (error) {
      console.error("Error logging out:", error.message);
    }
  };

  // Institute Rating
  const getInstituteRatingDetails = (value) => {
    if (value <= 1) return { label: "Terrible", color: "rgb(255,140,90)" };
    if (value <= 2) return { label: "Awful", color: "rgb(255,178,52)" };
    if (value <= 3) return { label: "Poor", color: "rgb(255,217,52)" };
    if (value <= 4) return { label: "Mediocre", color: "rgb(109, 162, 84)" };
    return { label: "Excellent", color: "rgb(46, 187, 91)" };
  };

  // Teacher Rating
  const getTeacherRatingDetails = (value) => {
    if (value <= 1) return { label: "Poor", color: "rgb(230, 46, 0)" };
    if (value <= 2) return { label: "Average", color: "rgb(230, 92, 0)" };
    if (value <= 3) return { label: "Good", color: "rgb(230, 138, 0)" };
    if (value <= 4) return { label: "Very Good", color: "rgb(230, 184, 0)" };
    return { label: "Excellent", color: "rgb(230, 230, 0)" };
  };

  /*
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login");
    } else if (user) {
      fetchReviews();
    }
  }, [user, navigate]); */

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      logoutUser();
      navigate("/login");
    } else {
      fetchReviews(); // No need to wait for `user`
    }
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await axios.get(
        "https://server.goatteachers.com/api/user/reviews",
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      setReviews({
        teacherReviews: response.data.teacherReviews,
        instituteReviews: response.data.instituteReviews,
      });
    } catch (error) {
      console.error("❌ Error fetching user reviews:", error);
      setError("Failed to load reviews");
    }
  };

  const handleEditClick = (review, type) => {
    setEditReview({ ...review, type });
    setUpdatedReview(review.comment);
    setShowEditModal(true);
  };

  const handleUpdateReview = async () => {
    try {
      if (!editReview || editReview.type !== "institute") {
        alert("Invalid institute review object.");
        return;
      }

      const token = localStorage.getItem("token");
      const endpoint = `https://server.goatteachers.com/api/user/reviews/institute/${editReview.institute_rating_id}`;

      // Must include all required fields — not just comment!
      const updatedData = {
        comment: updatedReview,
        quality_of_teaching: editReview.quality_of_teaching,
        curriculum: editReview.curriculum,
        infrastructure: editReview.infrastructure,
        campus_environment: editReview.campus_environment,
        communication: editReview.communication,
      };

      console.log("🔄 Sending update:", updatedData);

      await axios.put(endpoint, updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      alert("Review updated successfully!");
      fetchReviews();
      setShowEditModal(false);
    } catch (error) {
      console.error(
        "❌ Error updating review:",
        error.response?.data || error.message || error,
      );
      alert("Failed to update review. Please try again.");
    }
  };

  const handleDeleteClick = async (reviewId, type) => {
    if (window.confirm("Are you sure you want to delete this review?")) {
      try {
        const endpoint =
          type === "teacher"
            ? `https://server.goatteachers.com/api/user/reviews/teacher/${reviewId}`
            : `https://server.goatteachers.com/api/user/reviews/institute/${reviewId}`;

        await axios.delete(endpoint, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        alert("Review deleted successfully!");
        fetchReviews();
      } catch (error) {
        console.error("❌ Error deleting review:", error);
        alert("Failed to delete review. Please try again.");
      }
    }
  };

  const SectionCard = ({ title, children, icon }) => (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white">
          {icon}
        </div>
        <h4 className="text-lg md:text-xl font-semibold text-white/90">
          {title}
        </h4>
      </div>
      {children}
    </div>
  );

  const RatingPill = ({ label, color }) => (
    <span
      className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs md:text-sm font-medium ring-1 ring-white/10 bg-white/5"
      style={{ color }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: color }}
      ></span>
      {label}
    </span>
  );

  return (
    <>
      <section className="min-h-screen w-full bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
        {/* Header Glow */}
        <div className="pointer-events-none fixed inset-0 -z-10">
          <div className="absolute -top-24 left-1/2 h-96 w-[60rem] -translate-x-1/2 rounded-full bg-fuchsia-500/20 blur-3xl" />
          <div className="absolute top-40 right-10 h-72 w-72 rounded-full bg-indigo-500/10 blur-2xl" />
        </div>

        <div className="mx-auto max-w-6xl px-4 pb-20 pt-10 md:pt-14">
          {/* Error Alert */}
          {error && (
            <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
              {error}
            </div>
          )}

          {/* Profile Card */}
          <div className="mb-8 rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl backdrop-blur-xl md:p-8">
            {user ? (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="flex flex-col items-center md:items-start">
                  <div className="relative">
                    <img
                      src={
                        user.profile_picture ||
                        "https://cdn.creazilla.com/emojis/43426/man-student-emoji-clipart-md.png"
                      }
                      alt="Profile"
                      className="h-36 w-36 rounded-full object-cover ring-4 ring-white/10"
                    />
                    <span className="absolute -right-1 -bottom-1 inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-emerald-400 to-cyan-400 text-slate-900 text-xs font-bold shadow">
                      ⭐
                    </span>
                  </div>
                </div>

                <div className="md:col-span-2 flex flex-col justify-center">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="text-2xl md:text-3xl font-bold tracking-tight">
                      {user.name}
                    </h3>
                  </div>
                  <div className="mt-3 grid w-full grid-cols-1 gap-2 md:grid-cols-2">
                    <div className="text-sm text-white/70">
                      <span className="font-semibold text-white/80">
                        Email:
                      </span>{" "}
                      {user.email}
                    </div>
                  </div>

                  <div className="mt-5 flex flex-wrap gap-3">
                    <button
                      onClick={handleLogout}
                      className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-red-900/20 transition hover:scale-[1.02] focus:outline-none"
                    >
                      <span>Log Out</span>
                    </button>
                    <button
                      onClick={fetchReviews}
                      className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-4 py-2 text-sm font-medium text-white ring-1 ring-white/10 transition hover:bg-white/15"
                    >
                      Refresh Reviews
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <p>Loading...</p>
            )}
          </div>

          {/* Reviews Grid */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Institute Reviews */}
            <SectionCard title="Institute Reviews" icon={<span>🏫</span>}>
              {reviews.instituteReviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.instituteReviews.map((review, index) => (
                    <div
                      key={index}
                      className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-lg transition hover:bg-white/10"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h5 className="text-base md:text-lg font-semibold text-white/90">
                            {review.Institute.institute_name}
                          </h5>
                          <p className="mt-1 text-sm text-white/70">
                            {review.comment}
                          </p>
                        </div>
                        <div className="shrink-0 flex gap-2">
                          <button
                            onClick={() => handleEditClick(review, "institute")}
                            className="rounded-lg px-3 py-1.5 text-xs font-medium ring-1 ring-white/10 bg-white/10 hover:bg-white/15"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteClick(
                                review.institute_rating_id,
                                "institute",
                              )
                            }
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold bg-rose-500/20 text-rose-200 hover:bg-rose-500/30"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <RatingPill
                          label={`Quality of Teaching: ${
                            getInstituteRatingDetails(
                              review.quality_of_teaching,
                            ).label
                          }`}
                          color={
                            getInstituteRatingDetails(
                              review.quality_of_teaching,
                            ).color
                          }
                        />
                        <RatingPill
                          label={`Curriculum: ${
                            getInstituteRatingDetails(review.curriculum).label
                          }`}
                          color={
                            getInstituteRatingDetails(review.curriculum).color
                          }
                        />
                        <RatingPill
                          label={`Infrastructure: ${
                            getInstituteRatingDetails(review.infrastructure)
                              .label
                          }`}
                          color={
                            getInstituteRatingDetails(review.infrastructure)
                              .color
                          }
                        />
                        <RatingPill
                          label={`Campus Environment: ${
                            getInstituteRatingDetails(review.campus_environment)
                              .label
                          }`}
                          color={
                            getInstituteRatingDetails(review.campus_environment)
                              .color
                          }
                        />
                        <RatingPill
                          label={`Communication: ${
                            getInstituteRatingDetails(review.communication)
                              .label
                          }`}
                          color={
                            getInstituteRatingDetails(review.communication)
                              .color
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
                  No institute reviews yet.
                </div>
              )}
            </SectionCard>

            {/* Teacher Reviews */}
            <SectionCard title="Teacher Reviews" icon={<span>👩‍🏫</span>}>
              {reviews.teacherReviews.length > 0 ? (
                <div className="space-y-4">
                  {reviews.teacherReviews.map((review, index) => (
                    <div
                      key={index}
                      className="rounded-2xl border border-white/10 bg-white/5 p-4 shadow-lg transition hover:bg-white/10"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h5 className="text-base md:text-lg font-semibold text-white/90">
                            {review.Teacher.teacher_name} (
                            {review.Teacher.designation})
                          </h5>
                          <p className="mt-1 text-sm text-white/70">
                            {review.comment}
                          </p>
                        </div>
                        <div className="shrink-0 flex gap-2">
                          <button
                            onClick={() => handleEditClick(review, "teacher")}
                            className="rounded-lg px-3 py-1.5 text-xs font-medium ring-1 ring-white/10 bg-white/10 hover:bg-white/15"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteClick(
                                review.teacher_rating_id,
                                "teacher",
                              )
                            }
                            className="rounded-lg px-3 py-1.5 text-xs font-semibold bg-rose-500/20 text-rose-200 hover:bg-rose-500/30"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <RatingPill
                          label={`Proficiency: ${
                            getTeacherRatingDetails(review.proficiency_rating)
                              .label
                          }`}
                          color={
                            getTeacherRatingDetails(review.proficiency_rating)
                              .color
                          }
                        />
                        <RatingPill
                          label={`Connection: ${
                            getTeacherRatingDetails(review.connection_rating)
                              .label
                          }`}
                          color={
                            getTeacherRatingDetails(review.connection_rating)
                              .color
                          }
                        />
                        <RatingPill
                          label={`Clarity: ${
                            getTeacherRatingDetails(review.clarity_rating).label
                          }`}
                          color={
                            getTeacherRatingDetails(review.clarity_rating).color
                          }
                        />
                        <RatingPill
                          label={`Behavior: ${
                            getTeacherRatingDetails(review.behavior_rating)
                              .label
                          }`}
                          color={
                            getTeacherRatingDetails(review.behavior_rating)
                              .color
                          }
                        />
                        <RatingPill
                          label={`Supportiveness: ${
                            getTeacherRatingDetails(
                              review.supportiveness_rating,
                            ).label
                          }`}
                          color={
                            getTeacherRatingDetails(
                              review.supportiveness_rating,
                            ).color
                          }
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/80">
                  No teacher reviews yet.
                </div>
              )}
            </SectionCard>
          </div>
        </div>

        {/* Edit Review Modal */}
        {showEditModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setShowEditModal(false)}
            />
            <div className="relative z-10 w-full max-w-xl rounded-2xl border border-white/10 bg-slate-900 p-5 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold">Edit Review</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="rounded-lg px-2 py-1 text-sm text-white/70 hover:bg-white/10"
                >
                  ✕
                </button>
              </div>
              <label className="mb-2 block text-sm text-white/80">
                Review Comment
              </label>
              <textarea
                rows={4}
                value={updatedReview}
                onChange={(e) => setUpdatedReview(e.target.value)}
                className="block w-full rounded-xl border border-white/10 bg-white/5 p-3 text-sm outline-none placeholder:text-white/40 focus:ring-2 focus:ring-fuchsia-500"
                placeholder="Update your thoughts..."
              />
              <div className="mt-4 flex justify-end gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="rounded-xl px-4 py-2 text-sm font-medium ring-1 ring-white/10 bg-white/5 hover:bg-white/10"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateReview}
                  className="rounded-xl bg-gradient-to-br from-indigo-500 to-fuchsia-600 px-4 py-2 text-sm font-semibold text-white shadow-lg hover:scale-[1.01]"
                >
                  Update Review
                </button>
              </div>
            </div>
          </div>
        )}

        <Footer />
      </section>
    </>
  );
}

export default UserProfile;
